import { OPS, MSG_SOURCE } from "./messageOps";
import { getRecipientPasswords } from "../api/passwordDatabaseApi";
import { getUnlockMaterial } from "../api/passwordManagerSecurityApi";
import * as crypto from "../utils/crypto";

/**
 * Sidebar Bridge
 * Processes incoming bridge requests from the Password Manager dialog.
 * The sidebar is the single Crypto Host – all crypto operations run here.
 *
 * Dual transport:
 * - Production (Office Dialog API): Messages arrive via DialogMessageReceived,
 *   responses sent via dialog.messageChild()
 * - Dev mode (window.open): Messages arrive via window "message" event,
 *   responses sent via dialogWindow.postMessage()
 */

/**
 * Handles a single bridge message and returns the response payload.
 * This is transport-agnostic – the caller is responsible for sending the response.
 *
 * @param {object} message - Parsed message with { op, correlationId, payload }
 * @param {object} cryptoState - Current crypto state from CryptoContext
 * @param {object} cryptoState.isUnlocked
 * @param {object} cryptoState.masterKey - Uint8Array
 * @param {object} cryptoState.privateKey - PEM string
 * @param {object} cryptoState.passwortDatenbankId
 * @param {object} cryptoState.masterKeyVersion
 * @param {object} cryptoState.isOwner
 * @param {object} cryptoState.activeUserCount
 * @param {object} cryptoState.oid
 * @param {object} cryptoActions - Actions from CryptoContext
 * @param {Function} cryptoActions.unlock
 * @param {Function} cryptoActions.lock
 * @returns {Promise<object>} Response to send back: { source, correlationId, success, data?, errorCode? }
 */
export async function handleBridgeMessage(message, cryptoState, cryptoActions) {
  const { op, correlationId, payload } = message;

  const respond = (success, data = null, errorCode = null) => ({
    source: MSG_SOURCE,
    correlationId,
    success,
    data,
    errorCode,
  });

  try {
    switch (op) {
      case OPS.PING:
        return respond(true, { alive: true });

      case OPS.READY:
        // Dialog signals it is ready – respond with current state
        return respond(true, getStateSnapshot(cryptoState));

      case OPS.GET_STATE:
        return respond(true, getStateSnapshot(cryptoState));

      case OPS.UNLOCK_REQUEST: {
        if (!payload?.password) {
          return respond(false, null, "MISSING_PASSWORD");
        }
        const result = await cryptoActions.unlock(payload.password);
        if (result.success) {
          // Return the updated state after unlock (caller must re-read from context)
          // We return a flag; the dialog should send GET_STATE to get fresh state
          return respond(true, { unlocked: true });
        }
        return respond(false, { error: result.error }, "UNLOCK_FAILED");
      }

      case OPS.LOCK_REQUEST:
        cryptoActions.lock();
        return respond(true, { locked: true });

      case OPS.DECRYPT_MANY: {
        if (!cryptoState.isUnlocked || !cryptoState.masterKey) {
          return respond(false, null, "NOT_UNLOCKED");
        }
        if (!Array.isArray(payload?.entries)) {
          return respond(false, null, "INVALID_PAYLOAD");
        }

        const decrypted = [];
        for (const entry of payload.entries) {
          try {
            const result = await crypto.decryptRecipientPassword(
              entry.encryptedEmpfaengerEmail,
              entry.encryptedPasswort,
              entry.iv,
              cryptoState.masterKey,
            );
            decrypted.push({
              id: entry.empfaengerPasswortId || entry.id,
              email: result.email,
              password: result.password,
            });
          } catch (decryptErr) {
            console.warn("[SidebarBridge] Failed to decrypt entry:", decryptErr);
            decrypted.push({
              id: entry.empfaengerPasswortId || entry.id,
              email: null,
              password: null,
              error: "DECRYPT_FAILED",
            });
          }
        }
        return respond(true, { entries: decrypted });
      }

      case OPS.ENCRYPT_MANY: {
        if (!cryptoState.isUnlocked || !cryptoState.masterKey) {
          return respond(false, null, "NOT_UNLOCKED");
        }
        if (!Array.isArray(payload?.entries)) {
          return respond(false, null, "INVALID_PAYLOAD");
        }

        const encrypted = [];
        for (const entry of payload.entries) {
          const result = await crypto.encryptRecipientPassword(
            entry.email,
            entry.password,
            cryptoState.masterKey,
          );
          encrypted.push({
            encryptedEmpfaengerEmail: result.encryptedEmail,
            encryptedPasswort: result.encryptedPassword,
            iv: result.iv,
          });
        }
        return respond(true, { entries: encrypted });
      }

      case OPS.DECRYPT_PREFILL: {
        if (!cryptoState.isUnlocked || !cryptoState.masterKey) {
          return respond(false, null, "NOT_UNLOCKED");
        }
        if (!payload?.normalizedRecipients) {
          return respond(false, null, "INVALID_PAYLOAD");
        }

        const { oid, passwortDatenbankId, masterKey } = cryptoState;
        if (!oid || !passwortDatenbankId) {
          return respond(false, null, "NO_DATABASE");
        }

        // Fetch all encrypted passwords
        const pwResult = await getRecipientPasswords(oid, passwortDatenbankId);
        if (!pwResult.success) {
          return respond(false, null, "FETCH_PASSWORDS_FAILED");
        }
        const encryptedPasswords = pwResult.data;

        // Decrypt and find match
        for (const entry of encryptedPasswords) {
          try {
            const decrypted = await crypto.decryptRecipientPassword(
              entry.encryptedEmpfaengerEmail,
              entry.encryptedPasswort,
              entry.iv,
              masterKey,
            );
            if (decrypted.email === payload.normalizedRecipients) {
              return respond(true, {
                found: true,
                password: decrypted.password,
                empfaengerPasswortId: entry.empfaengerPasswortId,
              });
            }
          } catch (decryptErr) {
            console.warn("[SidebarBridge] Failed to decrypt entry during prefill:", decryptErr);
          }
        }

        // No match found
        return respond(true, { found: false, password: null });
      }

      case OPS.DECRYPT_MASTER_KEY: {
        // Decrypt MasterKey for the current active DB using the sidebar's private key.
        // Used by dialog pages after a DB switch (e.g. ZugriffsverwaltungPage).
        if (!cryptoState.isUnlocked || !cryptoState.privateKey) {
          return respond(false, null, "NOT_UNLOCKED");
        }
        const { oid: dmkOid } = cryptoState;
        if (!dmkOid) {
          return respond(false, null, "NO_GRAPH_ID");
        }

        const unlockResp = await getUnlockMaterial(dmkOid);
        if (!unlockResp.success || !unlockResp.data?.encryptedMasterKey) {
          return respond(false, null, "NO_ENCRYPTED_MASTER_KEY");
        }

        const decryptedMk = await crypto.decryptMasterKeyWithPrivateKey(
          unlockResp.data.encryptedMasterKey,
          cryptoState.privateKey,
        );

        // Return Base64-encoded MasterKey so it can be reconstructed in the dialog
        const mkBase64 = btoa(String.fromCharCode(...decryptedMk));
        return respond(true, {
          masterKeyBase64: mkBase64,
          masterKeyVersion: unlockResp.data.masterKeyVersion || 1,
        });
      }

      case OPS.ENCRYPT_NEW_MASTER_KEY: {
        // Generate a new MasterKey, encrypt it with the user's public key (from backend).
        // Returns the encrypted MasterKey + envelope for the dialog to send to the backend.
        if (!cryptoState.isUnlocked) {
          return respond(false, null, "NOT_UNLOCKED");
        }
        const { oid: enmkOid } = cryptoState;
        if (!enmkOid) {
          return respond(false, null, "NO_GRAPH_ID");
        }

        const enmkUnlockResp = await getUnlockMaterial(enmkOid);
        if (!enmkUnlockResp.success || !enmkUnlockResp.data?.publicKey) {
          return respond(false, null, "NO_PUBLIC_KEY");
        }

        const newMk = crypto.generateMasterKey();
        const encMk = await crypto.encryptMasterKeyWithPublicKey(
          newMk,
          enmkUnlockResp.data.publicKey,
        );
        const env = crypto.createMasterKeyEnvelope(1, 1);

        // Return everything the dialog needs to call createOwnDatabase
        const newMkBase64 = btoa(String.fromCharCode(...newMk));
        return respond(true, {
          masterKeyBase64: newMkBase64,
          encryptedMasterKey: encMk,
          masterKeyEnvelope: JSON.stringify(env),
          publicKey: enmkUnlockResp.data.publicKey,
        });
      }

      default:
        console.warn("[SidebarBridge] Unknown operation:", op);
        return respond(false, null, "UNKNOWN_OP");
    }
  } catch (err) {
    console.error("[SidebarBridge] Error handling operation:", op, err);
    return respond(false, { error: err.message }, "INTERNAL_ERROR");
  }
}

/**
 * Creates a snapshot of crypto state safe to send over the bridge.
 * NEVER includes masterKey bytes or privateKey – only metadata.
 */
function getStateSnapshot(cryptoState) {
  return {
    isUnlocked: cryptoState.isUnlocked,
    passwortDatenbankId: cryptoState.passwortDatenbankId,
    masterKeyVersion: cryptoState.masterKeyVersion,
    isOwner: cryptoState.isOwner,
    activeUserCount: cryptoState.activeUserCount,
    isPaid: cryptoState.isPaid,
  };
}

/**
 * Sets up a dev-mode message listener on window for postMessage bridge.
 * Used when the dialog is opened via window.open() (development mode).
 *
 * @param {Function} getCryptoState - Returns current crypto state
 * @param {object} cryptoActions - { unlock, lock }
 * @param {Window} dialogWindow - Reference to the dialog popup window
 * @returns {Function} cleanup - Call to remove the listener
 */
export function setupDevModeBridge(getCryptoState, cryptoActions, dialogWindow) {
  const handler = async (event) => {
    // Only process messages from our bridge
    if (!event.data || event.data.source !== MSG_SOURCE) return;
    if (!event.data.op || !event.data.correlationId) return;

    const response = await handleBridgeMessage(
      event.data,
      getCryptoState(),
      cryptoActions,
    );

    // Send response back to the dialog window
    if (dialogWindow && !dialogWindow.closed) {
      dialogWindow.postMessage(response, "*");
    }
  };

  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}

/**
 * Processes a bridge message received via Office DialogMessageReceived.
 * The caller is responsible for sending the response via dialog.messageChild().
 *
 * @param {string} messageString - JSON string from DialogMessageReceived
 * @param {Function} getCryptoState - Returns current crypto state
 * @param {object} cryptoActions - { unlock, lock }
 * @returns {Promise<string|null>} JSON response string to send via messageChild, or null if not a bridge message
 */
export async function handleOfficeDialogMessage(messageString, getCryptoState, cryptoActions) {
  let parsed;
  try {
    parsed = JSON.parse(messageString);
  } catch (_) {
    return null; // Not JSON – not a bridge message
  }

  if (parsed.source !== MSG_SOURCE || !parsed.op || !parsed.correlationId) {
    return null; // Not a bridge message
  }

  const response = await handleBridgeMessage(parsed, getCryptoState(), cryptoActions);
  return JSON.stringify(response);
}
